<template>
	<v-card-text>
		<v-row>
			<!-- Left Column: Encrypt & Verify -->
			<v-col
				cols="12"
				md="6"
			>
				<div class="text-subtitle-1 font-weight-medium mb-1">
					Encrypt & Verify
				</div>
				<div
					class="text-caption text--secondary mb-3"
					style="min-height: 2.5em; line-height: 1.25em;"
				>
					Test encrypting text and verify it decrypts correctly
				</div>

				<TextField
					v-model="testString"
					label="Test String"
					placeholder="Enter text to test"
					class="mb-2"
				/>

				<SecondaryActionButton
					block
					color="secondary"
					:disabled="disabled || isTesting || !testString"
					:loading="isTesting"
					@click="handleEncryptTest"
				>
					Encrypt & Decrypt
				</SecondaryActionButton>

				<encryption-test-result
					v-if="encryptResult"
					:result="encryptResult"
					class="mt-3"
				/>
			</v-col>

			<!-- Right Column: Decrypt Existing -->
			<v-col
				cols="12"
				md="6"
			>
				<div class="text-subtitle-1 font-weight-medium mb-1">
					Decrypt Existing Data
				</div>
				<div
					class="text-caption text--secondary mb-3"
					style="min-height: 2.5em; line-height: 1.25em;"
				>
					Paste an encrypted string to verify your settings can decrypt it
				</div>

				<TextField
					v-model="encryptedInput"
					label="Encrypted String"
					placeholder="Paste encrypted text here"
					class="mb-2"
				/>

				<SecondaryActionButton
					block
					color="secondary"
					:disabled="disabled || isDecrypting || !encryptedInput"
					:loading="isDecrypting"
					@click="handleDecryptTest"
				>
					Decrypt
				</SecondaryActionButton>

				<encryption-test-result
					v-if="decryptResult"
					:result="decryptResult"
					:decrypt-only="true"
					class="mt-3"
				/>
			</v-col>
		</v-row>
	</v-card-text>
</template>

<script>
import { testEncryption } from '../../../../../shared/utils/api/encryption-keys.js'
import EncryptionTestResult from './encryption-test-result.vue'
import SecondaryActionButton from '../../../../../shared/components/secondary-action-button.vue'
import TextField from '../../../../../shared/components/text-field.vue'

export default {
	name: 'encryption-test-panel',

	components: {
		EncryptionTestResult,
		SecondaryActionButton,
		TextField
	},

	props: {
		encryptionTypeId: {
			type: Number,
			required: true
		},
		credentials: {
			type: Object,
			required: true
		},
		disabled: {
			type: Boolean,
			default: false
		}
	},

	data: function () {
		return {
			testString: 'Hello, World!',
			encryptedInput: '',
			isTesting: false,
			isDecrypting: false,
			encryptResult: null,
			decryptResult: null
		}
	},

	methods: {
		buildRequest: function (plainText, encryptedText) {
			const request = {
				encryptionTypeId: this.encryptionTypeId
			}

			if (this.encryptionTypeId === 1) {
				request.encryptionKey = this.credentials.encryptionKey
			} else {
				request.secret = this.credentials.secret
				request.salt = this.credentials.salt
				request.iv = this.credentials.iv
			}

			if (plainText) {
				request.plainText = plainText
			}
			if (encryptedText) {
				request.encryptedText = encryptedText
			}

			return request
		},

		handleEncryptTest: function () {
			const self = this
			self.isTesting = true
			self.encryptResult = null

			const request = self.buildRequest(self.testString, null)

			testEncryption(request)
				.then(function (result) {
					self.encryptResult = result
				})
				.catch(function (error) {
					let errorMsg = 'Test request failed'
					if (error.response && error.response.data && error.response.data.error) {
						errorMsg = error.response.data.error
					} else if (error.message) {
						errorMsg = error.message
					}
					self.encryptResult = {
						success: false,
						originalText: self.testString,
						encryptedText: null,
						decryptedText: null,
						error: errorMsg
					}
				})
				.finally(function () {
					self.isTesting = false
				})
		},

		handleDecryptTest: function () {
			const self = this
			self.isDecrypting = true
			self.decryptResult = null

			const request = self.buildRequest(null, self.encryptedInput)

			testEncryption(request)
				.then(function (result) {
					self.decryptResult = result
				})
				.catch(function (error) {
					let errorMsg = 'An error occurred during decryption'
					if (error.response && error.response.status === 400) {
						errorMsg = 'Could not decrypt the provided value'
					}
					self.decryptResult = {
						success: false,
						originalText: null,
						encryptedText: self.encryptedInput,
						decryptedText: null,
						error: errorMsg
					}
				})
				.finally(function () {
					self.isDecrypting = false
				})
		},

		reset: function () {
			this.testString = 'Hello, World!'
			this.encryptedInput = ''
			this.encryptResult = null
			this.decryptResult = null
			this.isTesting = false
			this.isDecrypting = false
		}
	}
}
</script>
